import React, { useEffect } from 'react'

import {
  Input,
  Button,
  ButtonLink,
  Textarea,
  mediaQueries,
  palette,
} from '@centivo/react-components'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { useHistory, useLocation } from 'react-router-dom'
import styled from 'styled-components'

import { useMediaContext } from 'src/context/responsive'
import { contact } from './schema'

const Form = styled.form`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;

  ${Button} {
    margin: auto;
    min-width: 13em;

    @media ${mediaQueries.desktop} {
      margin: 1.5rem auto;
    }
  }

  ${Textarea} {
    height: 9.5rem;
    margin-bottom: 3rem;

    @media ${mediaQueries.desktop} {
      margin-bottom: 0;
    }
  }

  div${Input}, ${Textarea} {
    flex-basis: 100%;
    margin-top: 1.5rem;

    &:first-of-type {
      margin-top: 0;
    }

    @media ${mediaQueries.desktop} {
      margin-left: 0;
      margin-right: 0;

      &:nth-of-type(-n + 2) {
        margin-top: 0;
      }
    }
  }

  @media ${mediaQueries.desktop} {
    div${Input} {
      &:nth-of-type(-n + 2) {
        max-width: calc(50% - 0.5rem);
      }
    }
  }
`

const FormFooter = styled.div<{ shouldHideFooter?: boolean }>`
  display: flex;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  box-shadow: 0px 2px 20px rgba(0, 0, 0, 0.2);
  width: 100vw;
  justify-content: center;
  padding: 2rem 1.25rem 2.5rem;
  background-color: ${palette.neutral.white};

  ${Button}, ${ButtonLink} {
    box-sizing: border-box;
    width: 48%;
    min-width: 48%;
  }

  ${ButtonLink} {
    margin-right: 0.75rem;
  }

  /* ${Input}:focus-within ~ &&,
  ${Textarea}:focus-within ~ && {
    display: none;
  } */

  ${({ shouldHideFooter }) => shouldHideFooter && 'display: none'}
`

export type ContactValues = {
  firstName: string
  lastName: string
  employer: string
  email?: string
  phone?: string
  message: string
} & (
  | {
      phone: string
      email?: string
    }
  | {
      phone?: string
      email: string
    }
)

interface ContactProps {
  from?: string
}

const nameKeyFilter = [' ']

export const ContactForm: React.FC<ContactProps> = ({ from = 'modal' }) => {
  const {
    register,
    trigger,
    formState: { errors },
    clearErrors,
  } = useForm<ContactValues>({
    resolver: yupResolver(contact),
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
  })
  const { isMobileView, isKeyboardOpen } = useMediaContext()

  const history = useHistory()
  const location = useLocation<{ from?: string }>()

  const query = new URLSearchParams(window.location.search)
  query.set('contactSubmitted', 'true')
  query.set('from', from)

  const retUrl = `${window.location.origin}${
    window.location.pathname
  }?${query.toString()}`

  const goBack = () => history.push(location.state?.from || '/')

  const onSubmit: React.FormEventHandler = async e => {
    const isValid = await trigger()

    if (!isValid) {
      e.preventDefault()
    }
  }

  const onBlur = (el: keyof ContactValues) => () => {
    if (errors[el]) {
      trigger(el)
    }
  }

  const { phone, email } = errors
  useEffect(() => {
    if (phone?.type === 'required' && (!email || email.type !== 'required')) {
      clearErrors('phone')
    }

    if (email?.type === 'required' && (!phone || phone.type !== 'required')) {
      clearErrors('email')
    }
  }, [phone, email, clearErrors])

  return (
    <Form
      onSubmit={onSubmit}
      action="https://webto.salesforce.com/servlet/servlet.WebToCase?encoding=UTF-8"
      method="POST"
    >
      <input type="hidden" name="orgid" value="00D3h000002D4eC" />
      <input type="hidden" name="retURL" value={retUrl} />
      <Input
        {...register('firstName')}
        autoCapitalize="words"
        label="First name"
        placeholder="First name"
        id="firstName"
        errorText={errors.firstName?.message}
        keyFilter={nameKeyFilter}
        onBlur={onBlur('firstName')}
        isClearable
        float
      />
      <Input
        {...register('lastName')}
        label="Last name"
        placeholder="Last name"
        id="lastName"
        autoCapitalize="words"
        errorText={errors.lastName?.message}
        keyFilter={nameKeyFilter}
        onBlur={onBlur('lastName')}
        isClearable
        float
      />
      <Input
        {...register('employer')}
        label="Employer name"
        placeholder="Employer name"
        id="employer"
        errorText={errors.employer?.message}
        onBlur={onBlur('employer')}
        isClearable
        float
      />
      <Input
        {...register('email')}
        label="Email"
        placeholder="Email"
        id="email"
        type="email"
        hasError={!!errors.email?.message}
        errorText={errors.email?.message}
        onBlur={onBlur('email')}
        isClearable
        float
      />
      <Input
        {...register('phone')}
        id="phone"
        mask={'phone' as any}
        label="Phone number"
        placeholder="Phone number"
        errorText={errors.phone?.message}
        onBlur={onBlur('phone')}
        type="tel"
        isClearable
        float
      />
      <Textarea
        {...register('message')}
        placeholder="Your message. Please do not share personal health information."
        errorText={errors.message?.message}
        onBlur={onBlur('message')}
      />
      {isMobileView ? (
        <FormFooter shouldHideFooter={isKeyboardOpen}>
          <ButtonLink href="#" onClick={goBack} buttonType="secondary">
            Go back
          </ButtonLink>
          <Button name="submit" type="submit">
            Send message
          </Button>
        </FormFooter>
      ) : (
        <Button name="submit" type="submit" edgeType="default">
          Send message
        </Button>
      )}
    </Form>
  )
}
